import { Group, type Opportunity, OpportunityParticipant } from "@entities";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { CircularProgress, Stack } from "@mui/material";
import {
  groupOpportunityRepo,
  groupRepo,
  opportunityParticipantRepo,
} from "@repositories";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import {
  useQueryClient,
  type QueryObserverResult,
  type RefetchOptions,
} from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { type EntityFilter } from "remult";
import GroupCard from "../../../components/popups/group-card/GroupCard";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { ADMIN_BULK_ACTION_LIMIT } from "../../../helpers/admin-list-query";
import { invalidateEventParticipantQueries } from "../../../queries/invalidate-opportunities-table";
import { useGroupColumns } from "./columns";
import filters from "./filters";
import "./GroupsTable.scss";
import "./VolunteersGroupsTable.scss";

const ParticipantsGroupTable: FC<{
  opportunity?: Opportunity;
  refetchEvent: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<Opportunity | null | undefined, Error>>;
}> = ({ opportunity, refetchEvent }) => {
  const { HyperTable } = useCreateHyperTable(Group);
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();
  const queryClient = useQueryClient();
  const [isAddGroup, setIsAddGroup] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [group, setGroup] = useState<Group>();

  const buttons: ActionButton<Group>[] = useMemo(() => {
    return [
      {
        icon: <PersonRemoveIcon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;
          const selected = await groupRepo.find({
            where: whereToUpdate,
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
          });
          const truncated = selected.length > ADMIN_BULK_ACTION_LIMIT;
          const page = truncated
            ? selected.slice(0, ADMIN_BULK_ACTION_LIMIT)
            : selected;
          const volunteerOrgGroups = page.filter((row) => row.volunteerOrganizationId);
          const regularIds = page
            .filter((row) => !row.volunteerOrganizationId)
            .map((row) => row.id);
          if (page.length === 0) return;
          if (regularIds.length === 0) {
            setAlertText(
              truncated
                ? `קבוצה של ארגון מתנדבים נשארת במשימה. נבדקו ${ADMIN_BULK_ACTION_LIMIT} בכל פעולה, חזרו על הפעולה לשאר`
                : "קבוצה של ארגון מתנדבים נשארת במשימה עם המאצ'ים שלה",
            );
            setSeverity("warning");
            return;
          }
          const groupIdFilter = regularIds.length === 1 ? regularIds[0] : { $in: regularIds };
          await groupOpportunityRepo.deleteMany({
            where: {
              groupId: groupIdFilter,
              opportunityId: opportunity?.id,
            },
          });
          await opportunityParticipantRepo.deleteMany({
            where: OpportunityParticipant.hyperFilter({
              opportunity: { $is: { id: opportunity?.id } },
              group: { $is: { id: groupIdFilter } },
            }),
          });
          const limitNote = truncated
            ? ` הוסרו עד ${ADMIN_BULK_ACTION_LIMIT} בכל פעולה. חזרו על הפעולה לשאר.`
            : "";
          setAlertText(
            volunteerOrgGroups.length > 0
              ? `קבוצות רגילות הוסרו. קבוצה של ארגון מתנדבים נשארת עם המאצ'ים.${limitNote}`
              : `הקבוצה הוסרה מהמשימה בהצלחה.${limitNote}`,
          );
          setSeverity(
            volunteerOrgGroups.length > 0 || truncated ? "warning" : "success",
          );
          void invalidateEventParticipantQueries(queryClient);
          refetchEvent();
          HyperTable.context.current?.refetch("remove");
        },
        title: "הסרה ממשימה",
      },
      {
        title: "קישור הצטרפות לקבוצה",
        icon: <ContentCopyIcon />,
        onClick: async ({ whereToUpdate }) => {
          const group = await groupRepo.findOne({
            where: whereToUpdate
          });
          navigator.clipboard.writeText(
            `https://www.we-me.app/group/${group?.id}/join`,
          );
          setAlertText("הקישור הועתק ללוח");
          setSeverity("success");
          return {
            refetch: false,
          };
        },
      },
    ];
  }, [opportunity, HyperTable]);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId={`participants-groups-${opportunity?.id ?? "unknown"}`}
      findConfig={{
        where: {
          ...Group.hyperFilter({
            opportunities: {
              $some: {
                opportunityId: opportunity?.id,
              },
            },
            organization: { $id: currOrganization?.id },
          }),
          organization: { $id: currOrganization?.id },
        } as EntityFilter<Group>,
        enabled: !!currOrganization,
        include: {
          admin: true,
          organization: true,
        },
      }}
      onRowClick={(_, row) => {
        setIsAddGroup(true);
        setIsEdit(true);
        setGroup(row.original);
      }}
    >
      <HyperTable.Toolbar>
        {filters.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
          ),
        )}
      </HyperTable.Toolbar>
      {useGroupColumns(currOrganization, setAlertText, setSeverity).map(
        (column) => (
          <HyperTable.Column key={column.field} {...column} />
        ),
      )}
      <GroupCard
        isEdit={isEdit}
        isOpen={isAddGroup}
        onEditComplete={() => {
          HyperTable.context.current?.refetch("remove");
        }}
        onClose={() => {
          setIsAddGroup(false);
          setGroup(undefined);
        }}
        groupDetails={group}
      />
      <HyperTable.SelectColumn />
      <HyperTable.ActionsBar buttons={buttons} />
    </HyperTable>
  );
};

export default ParticipantsGroupTable;
